import { breakdownArithmetic } from './breakdownLine';
import { Pill } from './controls';

/**
 * §7.4's audit trail, opened under a row of the designer's preview: every event that reached this
 * player's total, and every figure that produced it.
 *
 * <b>The same payload the player portal reads, laid out for a different reader.</b> player-web's
 * `LeaderboardBreakdown` renders one person's season as cards at reading size — it is read once, by
 * the player it is about, on a phone. This is read by an operator who is *testing a system*: they
 * open a row because a number surprised them, they read it, and they go back to editing the
 * placement table. So it is dense, tabular and sits inside a pane a third of a screen wide, and it
 * leads with the arithmetic rather than with the sentence. Two layouts, one shape, one set of
 * server-computed figures.
 *
 * <b>Nothing here is calculated.</b> Every number is the engine's, and so is every explanation —
 * §7.4 is explicit that the sentence is not decoration, and a client wording the deal rule itself
 * would be a fourth implementation of §3.4 that nothing tests. What this file does is arrange them:
 * which multiplier to omit, which event to dim, and what to say when there is nothing to show.
 */

/** One event's contribution, declared structurally — the apps pass the DTO straight in. */
export interface PointsBreakdownEvent {
  eventId: string;
  eventName: string;
  eventDate: string;
  place: number | null;
  effectivePlace: number | null;
  dealSize: number;
  baseAward: number;
  fieldMultiplier: number;
  buyInMultiplier: number;
  scoringResultPoints: number;
  participationEntries: number;
  /**
   * §3.7. The entries the rule admitted before once per tournament capped them; equal to
   * `participationEntries` otherwise. Null on a breakdown stored before the field existed, and
   * absent from a payload built before it — both read as `participationEntries`.
   */
  participationEntriesAdmitted?: number | null;
  participationPoints: number;
  eventScore: number;
  counted: boolean;
  explanation: string;
  warnings: string[];
}

export interface PointsPlayerBreakdown {
  playerId: string;
  playerName: string;
  rank: number;
  isTied: boolean;
  totalPoints: number;
  scoringResultPoints: number;
  participationPoints: number;
  eventsScored: number;
  decimalPlaces: number;
  events: PointsBreakdownEvent[];
}

export type PointsBreakdownState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  /** `null` is a player the previewed board does not hold — an answer, not a failure. */
  | { status: 'ready'; result: PointsPlayerBreakdown | null };

/**
 * §9.8's drill-down. Rendered inside the standings table, spanning every column, under the row it
 * belongs to — so the working stays attached to the player it explains while the board around it
 * keeps its place.
 */
export function PointsBreakdownPanel({ state }: { state: PointsBreakdownState }) {
  if (state.status === 'loading') {
    return <Note>Working out how these points were earned…</Note>;
  }

  if (state.status === 'error') {
    return (
      <Note role="alert">
        {state.message} Nothing about the board above has changed — this is the working that
        couldn’t be fetched.
      </Note>
    );
  }

  if (state.result === null) {
    return <Note>This player isn’t on the board these events produce, so there is nothing to break down.</Note>;
  }

  const breakdown = state.result;
  const places = breakdown.decimalPlaces;
  const dropped = breakdown.events.filter((x) => !x.counted).length;

  if (breakdown.events.length === 0) {
    return <Note>Nothing in scope has a result for this player.</Note>;
  }

  return (
    <div className="space-y-2.5 border-l-2 border-line bg-ground/40 px-4 py-3">
      {/* The total split the way §3.8 splits it, so the working below has a shape to fit into
          before any of it is read: how much came from finishing, how much from entering, and how
          many of the events listed actually reached the total. */}
      <p className="text-[11.5px] leading-relaxed text-ink-800/70">
        <span className="num font-semibold text-ink-950">{points(breakdown.totalPoints, places)}</span>{' '}
        in total — <span className="num">{points(breakdown.scoringResultPoints, places)}</span> from
        finishes and <span className="num">{points(breakdown.participationPoints, places)}</span> from
        entries, over {breakdown.events.length} event{breakdown.events.length === 1 ? '' : 's'}
        {dropped > 0 ? ` (${dropped} not counted)` : ''}.
      </p>

      <ol className="space-y-2">
        {breakdown.events.map((event) => (
          <PointsBreakdownEventRow key={event.eventId} event={event} places={places} />
        ))}
      </ol>
    </div>
  );
}

/**
 * One event's working.
 *
 * The badge is the visual hook for the whole feature: where the deal rule moved a player, it says
 * so as `1st → 3rd` before anybody reads a word. Where it did not, there is no badge — a panel
 * marking every ordinary finish "1st → 1st" spends the reader's attention on the case that needs
 * none.
 *
 * §3.9's `bestN` drops events from the total but not from the audit trail, so an uncounted event is
 * dimmed and labelled rather than omitted: "why didn't my ninth-best result count" is the second
 * question this panel will be asked, and an absent event answers it worse than silence.
 *
 * <b>Exported, because ratings §7 nests this very object inside a rating's breakdown and requires it
 * rendered VERBATIM — by the existing component rather than by a second one.</b> The fantasy F6
 * defect was a label reading the wrong field and the fix rule was one pure function both call sites
 * use; this is that rule applied to a component. A rating's own row wraps it with the period, the
 * decayed figure and §3.3's selection note, and touches nothing inside it.
 *
 * <b>`counted` is deliberately left alone here and is always `true` on a rating's rows</b>, because
 * they reach this through `PointsEngine.ScoreEvents` and nothing has aggregated them — so the "Not
 * counted" pill and the dimming below never fire there, which is exactly what §7 asks for: the field
 * is carried in the data and not surfaced. A rating's authoritative answer is `selected`, and its own
 * row is what dims.
 */
export function PointsBreakdownEventRow({
  event,
  places,
  dimmed = false,
  children,
}: {
  event: PointsBreakdownEvent;
  places: number;

  /**
   * Dim this row for a reason of the caller's own.
   *
   * <b>It exists because a rating's reason is not this component's.</b> `counted` is always true on a
   * rating's rows, so the dimming below never fires there — what dims a rating's row is §3.3's
   * `selected`, which is a fact about the rating layer and not about the points engine. OR-ed rather
   * than replacing, so the points designer's own dimming is untouched.
   */
  dimmed?: boolean;

  /**
   * The caller's own material, rendered <b>inside this list item</b>, under the engine's sentence.
   *
   * <b>A slot rather than a wrapper, because this returns an `li`.</b> Ratings §7 needs to put its
   * own annotations — the decayed figure, §3.3's selection note — beside the object this renders, and
   * nesting one list item inside another to do that is not HTML. A slot keeps the nested object
   * rendered by this component verbatim, which is what §7 asks for, while letting a rating say the
   * things only a rating knows.
   */
  children?: React.ReactNode;
}) {
  const moved =
    event.place !== null && event.effectivePlace !== null && event.place !== event.effectivePlace;

  return (
    <li className={`border-t border-line/60 pt-2 ${event.counted && !dimmed ? '' : 'opacity-55'}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-[12px] text-ink-950">{event.eventName}</span>
        <span className="num shrink-0 text-[12px] font-semibold text-ink-950">
          {points(event.eventScore, places)}
        </span>
      </div>

      <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[11px] text-ink-800/55">
        <span className="num">{event.eventDate}</span>
        <span>·</span>
        <span>{event.place === null ? 'did not cash' : `finished ${ordinal(event.place)}`}</span>
        {moved ? (
          <Pill>
            {ordinal(event.place!)} → scored as {ordinal(event.effectivePlace!)}
          </Pill>
        ) : null}
        {event.counted ? null : <Pill tone="warn">Not counted</Pill>}
      </div>

      <Arithmetic event={event} places={places} />

      {/* The server's sentence, verbatim. §7.4: not decoration — and the one thing on this panel
          that says *why* rather than *what*. */}
      <p className="mt-1 text-[11.5px] leading-relaxed text-ink-900">{event.explanation}</p>

      {event.warnings.length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {event.warnings.map((warning) => (
            <li key={warning} className="text-[11px] text-warn">
              {warning}
            </li>
          ))}
        </ul>
      ) : null}

      {children}
    </li>
  );
}

/** The compact line, assembled by `breakdownArithmetic` so player-web and this panel agree. */
function Arithmetic({ event, places }: { event: PointsBreakdownEvent; places: number }) {
  const line = breakdownArithmetic(event, places);
  if (line === null) return null;

  return <p className="num mt-1 text-[11.5px] text-ink-800/75">{line}</p>;
}

function Note({ children, role }: { children: React.ReactNode; role?: 'alert' }) {
  return (
    <p role={role} className="border-l-2 border-line bg-ground/40 px-4 py-3 text-[11.5px] text-ink-800/60">
      {children}
    </p>
  );
}

/**
 * Rounded away from zero, matching the engine — .NET's `MidpointRounding.AwayFromZero`, not
 * JavaScript's default. `toFixed` alone rounds a binary approximation, so a figure the server
 * renders as 90.13 inside its explanation can come out 90.12 in the line above it, which is the one
 * discrepancy on this panel a reader would take as an error rather than as rounding.
 */
function points(value: number, places: number): string {
  const factor = 10 ** places;
  return ((Math.sign(value) * Math.round(Math.abs(value) * factor)) / factor).toFixed(places);
}

function ordinal(n: number): string {
  const suffix =
    n % 100 >= 11 && n % 100 <= 13 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';

  return `${n}${suffix}`;
}
